import React, { useMemo, useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import { Box, Text, Center, Icon, useToken } from '@chakra-ui/react';
import { InfoOutlineIcon } from '@chakra-ui/icons';
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip as RechartsTooltip,
  CartesianGrid,
  Legend,
  ComposedChart,
  Label,
  ReferenceLine
} from 'recharts';

// Okabe–Ito colorblind-safe palette for multi-series readability
const COLORBLIND_PALETTE = [
  '#0072B2', // blue
  '#E69F00', // orange
  '#009E73', // green
  '#D55E00', // vermillion
  '#56B4E9', // sky blue
  '#CC79A7', // purple
  '#F0E442', // yellow
  '#000000', // black
];

// Compact number formatter, e.g. 1.2k, 3.4M
const formatNumberCompact = (value) => {
  if (value === undefined || value === null || isNaN(value)) return value;
  const abs = Math.abs(value);
  if (abs >= 1.0e9) return `${(value / 1.0e9).toFixed(1)}B`;
  if (abs >= 1.0e6) return `${(value / 1.0e6).toFixed(1)}M`;
  if (abs >= 1.0e3) return `${(value / 1.0e3).toFixed(1)}k`;
  return `${value}`;
};

function TrendChart({ data, series = [], xKey = 'period', height = 350, chartType = 'bar', palette = 'colorblind', showLegend = true, usePatterns = false, syncId }) {
  // Resolve theme tokens for consistent styling (must be before any early returns)
  const [gridColor, tickColor, labelColor, cursorFill] = useToken('colors', [
    'gray.200',
    'gray.600',
    'gray.600',
    'gray.50',
  ]);
  // Default series palette from theme tokens or colorblind-safe palette
  const defaultPaletteKeys = ['green.500', 'orange.400', 'blue.500', 'purple.500', 'yellow.500'];
  const themePalette = useToken('colors', defaultPaletteKeys);
  const defaultPalette = palette === 'colorblind' ? COLORBLIND_PALETTE : themePalette;
  // Resolve provided color tokens (e.g., 'green.500') to hex; keep hex if already provided
  const providedColorKeys = useMemo(() => (series || []).map(s => s.color).filter(Boolean), [series]);
  const providedResolved = useToken('colors', providedColorKeys);
  const colorMap = useMemo(() => {
    const map = new Map();
    providedColorKeys.forEach((key, idx) => { map.set(key, providedResolved[idx] || key); });
    return map;
  }, [providedColorKeys, providedResolved]);
  // Legend toggle state
  const [hiddenKeys, setHiddenKeys] = useState(() => new Set());
  const labelToKey = useMemo(() => {
    const map = new Map();
    (series || []).forEach(s => { map.set(s.label, s.key); });
    return map;
  }, [series]);
  const handleLegendClick = useCallback((entry) => {
    const label = entry && (entry.value || entry.payload?.value);
    const dataKey = labelToKey.get(label) || entry?.dataKey;
    if (!dataKey) return;
    setHiddenKeys(prev => {
      const next = new Set(prev);
      if (next.has(dataKey)) next.delete(dataKey); else next.add(dataKey);
      return next;
    });
  }, [labelToKey]);

  // Graceful empty state
  if (!data || data.length === 0) {
    return (
      <Center w="100%" h={{ base: '200px', md: `${height}px` }} flexDirection="column" bg="white" borderRadius="xl">
        <Icon as={InfoOutlineIcon} boxSize={8} color="gray.300" mb={2} />
        <Text color="gray.400" fontSize="lg">No trend data available</Text>
      </Center>
    );
  }

  // Choose chart type
  let ChartComponent = BarChart;
  if (chartType === 'line') ChartComponent = LineChart;
  if (chartType === 'mixed') ChartComponent = ComposedChart;

  // X-axis tick formatter for better readability
  const xTickFormatter = (tick) => {
    // If tick is a date string, abbreviate or format as needed
    if (typeof tick === 'string' && tick.length >= 10) {
      return tick.slice(5); // e.g., '2025-06-30' -> '06-30'
    }
    return tick;
  };

  return (
    <Box w="100%" h={{ base: '250px', md: `${height}px` }} bg="white" borderRadius="xl" border="none" p={0}>
      <ResponsiveContainer width="100%" height="100%">
        <ChartComponent data={data} margin={{ top: 10, right: 20, left: 0, bottom: 30 }} barCategoryGap="24%" barGap={2} syncId={syncId}>
          <CartesianGrid strokeDasharray="3 3" stroke={gridColor} />
          <XAxis
            dataKey={xKey}
            tick={{ fill: tickColor, fontSize: 12 }}
            axisLine={false}
            tickLine={false}
            tickFormatter={xTickFormatter}
          >
            <Label value="Date" offset={-10} position="insideBottom" fill={labelColor} fontSize={13} />
          </XAxis>
          <YAxis tick={{ fill: tickColor, fontSize: 12 }} axisLine={false} tickLine={false} tickFormatter={formatNumberCompact}>
            <Label value="Test Count" angle={-90} position="insideLeft" fill={labelColor} fontSize={13} />
          </YAxis>
          {/* Zero reference line for context */}
          <ReferenceLine y={0} stroke={gridColor} strokeDasharray="3 3" ifOverflow="extendDomain" />
          {/* Gradients and optional pattern fills for bars */}
          <defs>
            {series.map((s, idx) => {
              if (s.type !== 'bar') return null;
              const resolvedColor = s.color
                ? (colorMap.get(s.color) || s.color)
                : defaultPalette[idx % defaultPalette.length];
              const id = `bar-grad-${idx}`;
              return (
                <linearGradient key={id} id={id} x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor={resolvedColor} stopOpacity={0.9} />
                  <stop offset="100%" stopColor={resolvedColor} stopOpacity={0.2} />
                </linearGradient>
              );
            })}
            {usePatterns && series.map((s, idx) => {
              if (s.type !== 'bar') return null;
              const patternId = `bar-pattern-${idx}`;
              const resolvedColor = s.color
                ? (colorMap.get(s.color) || s.color)
                : defaultPalette[idx % defaultPalette.length];
              return (
                <pattern key={patternId} id={patternId} patternUnits="userSpaceOnUse" width="6" height="6" patternTransform="rotate(45)">
                  <rect width="6" height="6" fill={resolvedColor} opacity="0.15" />
                  <line x1="0" y1="0" x2="0" y2="6" stroke={resolvedColor} strokeWidth="2" />
                </pattern>
              );
            })}
          </defs>
          <RechartsTooltip
            contentStyle={{ borderRadius: 12, border: `1px solid ${gridColor}`, background: '#fff', color: '#1A202C' }}
            labelStyle={{ color: labelColor }}
            cursor={{ fill: cursorFill }}
            formatter={(value, name) => {
              const v = typeof value === 'number' ? `${value.toLocaleString()} (${formatNumberCompact(value)})` : value;
              return [v, name];
            }}
          />
          {showLegend && (
            <Legend
              verticalAlign="bottom"
              height={40}
              iconType="circle"
              onClick={handleLegendClick}
              formatter={(value) => {
                const key = labelToKey.get(value);
                const isHidden = key ? hiddenKeys.has(key) : false;
                return (
                  <span style={{ color: isHidden ? '#A0AEC0' : '#2D3748', textDecoration: isHidden ? 'line-through' : 'none' }}>{value}</span>
                );
              }}
            />
          )}
          {series.map((s, idx) => {
            const resolvedColor = s.color
              ? (colorMap.get(s.color) || s.color)
              : defaultPalette[idx % defaultPalette.length];
            const isHidden = hiddenKeys.has(s.key);
            return s.type === 'bar' ? (
              <Bar
                key={s.key}
                dataKey={s.key}
                fill={usePatterns ? `url(#bar-pattern-${idx})` : `url(#bar-grad-${idx})`}
                radius={[8, 8, 0, 0]}
                name={s.label}
                hide={isHidden}
              />
            ) : (
              <Line
                key={s.key}
                type="monotone"
                dataKey={s.key}
                stroke={resolvedColor}
                strokeWidth={2.5}
                dot={false}
                activeDot={{ r: 5, strokeWidth: 2 }}
                name={s.label}
                hide={isHidden}
              />
            );
          })}
        </ChartComponent>
      </ResponsiveContainer>
    </Box>
  );
}

TrendChart.propTypes = {
  data: PropTypes.array.isRequired,
  series: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
      color: PropTypes.string,
      type: PropTypes.oneOf(['bar', 'line']),
    })
  ).isRequired,
  xKey: PropTypes.string,
  height: PropTypes.number,
  chartType: PropTypes.oneOf(['bar', 'line', 'mixed']),
  usePatterns: PropTypes.bool,
  syncId: PropTypes.string,
};

TrendChart.defaultProps = {
  xKey: 'period',
  height: 350,
  chartType: 'bar',
  usePatterns: true,
};

export default TrendChart; 